import React from "react";

import Divider from "veneer/Divider";
import Stack from "veneer/Stack";
import TextButton from "veneer/TextButton";
import { Colors } from "veneer/Theme";
import Typography from "veneer/Typography";

import type { DeviceMetricFilter } from "cortex/ui/src/fleet/view/device/model";
import { useAppSelector } from "cortex/ui/src/hooks";
import { deviceVersionMetricTotalsSelector } from "cortex/ui/src/redux-store/devices/selectors";
import { Panel } from "cortex/ui/src/view/layout";

interface StatusRowProps {
  count: number;
  label: string;
  onCountClick?: () => void;
}

export interface DeviceMetricsPanelProps {
  metricsTotal?: ReturnType<typeof deviceVersionMetricTotalsSelector>;
  onMetricClick?: (metric: DeviceMetricFilter) => void;
  onNeverConnectedClick?: () => void;
}

function StatusRow({ count, label, onCountClick }: StatusRowProps) {
  return (
    <Stack
      alignItems="center"
      direction="row"
      gap={1}
      justifyContent="space-between"
      py={1}
    >
      <Typography color={Colors.gray1000} variant="h6">
        {label}
      </Typography>
      {onCountClick ? (
        <TextButton
          aria-label={`Filter devices by ${label}`}
          dataTestId={`device-metrics-count-${label
            .toLowerCase()
            .replace(/\s+/g, "-")}`}
          onClick={onCountClick}
        >
          {count.toLocaleString()}
        </TextButton>
      ) : (
        <Typography color={Colors.gray1000}>
          {count.toLocaleString()}
        </Typography>
      )}
    </Stack>
  );
}

export default function DeviceMetricsPanel({
  metricsTotal,
  onMetricClick,
  onNeverConnectedClick,
}: DeviceMetricsPanelProps) {
  const selectorMetricsTotal = useAppSelector(
    deviceVersionMetricTotalsSelector,
  );
  const deviceMetricsTotal = metricsTotal ?? selectorMetricsTotal;

  if (!deviceMetricsTotal) {
    return null;
  }

  return (
    <Panel mb={0}>
      <Stack alignItems="stretch" direction="column" gap={1}>
        <Stack divider={<Divider />} flex={2} spacing={0.5}>
          <StatusRow
            count={deviceMetricsTotal.upgradeAvailable}
            label="Upgrade Available"
            onCountClick={() => onMetricClick?.("Upgrade Available")}
          />
          <StatusRow
            count={deviceMetricsTotal.pendingVersionChange}
            label="Pending Version"
            onCountClick={() => onMetricClick?.("Pending Version")}
          />
          <StatusRow
            count={deviceMetricsTotal.neverConnected}
            label="Never Connected"
            onCountClick={onNeverConnectedClick}
          />
        </Stack>
      </Stack>
    </Panel>
  );
}
